import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useReactToPrint } from 'react-to-print';
import { loadStatementByCompany, loadNetPaidSince } from '../../../core/utils/installment';
import { groupStatementRows } from '../../../core/utils/statementGrouping';
import { loadBanksWithCurrentBalance } from '../../../core/utils/bank';
import { getInitialBalance } from '../../../core/utils/projectedCashFlow';
import { formatCurrency, formatDateBR } from '../../../core/utils/format';
import { ENTRY_TYPE, PERIOD_FILTERS, STATUS } from '../../../core/constants/financial';
import { todayISO, getRangeFromFilter, getPeriodLabel } from '../../../core/utils/financialPeriod';
import { useLogoPreloader } from '../../../core/hooks/useLogoPreloader';
import { useTableSort } from '../../../core/hooks/useTableSort';
import SortableTh from './SortableTh';
import BankStatementPrint from './BankStatementPrint';
import FinancialEntryModal from './FinancialEntryModal';

const PRINT_PAGE_STYLE = `
  @page { size: A4 portrait; margin: 10mm; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
`;

const SORT_ACCESSORS = {
  data: ({ inst }) => inst.payment_date || '',
  descricao: ({ inst }) => inst.financial_entry?.supplier_client || '',
  banco: ({ inst }) => inst.Bank?.name || '',
  tipo: ({ inst }) => inst.financial_entry?.type || '',
  // Com o sinal exibido na tela (+ entrada / − saída), senão saídas ordenam
  // como positivas e misturam com as entradas.
  valor: ({ inst }) => {
    const value = Number(inst.value) || 0;
    return inst.financial_entry?.type === ENTRY_TYPE.RECEITA ? value : -value;
  },
  saldo: ({ saldoAcumulado }) => saldoAcumulado,
};

const CashFlowModal = ({ isOpen, onClose, onNewEntry, onOpenProjected, onChanged }) => {
  const company = useSelector((state) => state.app.company || {});
  const [movements, setMovements] = useState([]);
  const [netSince, setNetSince] = useState(0);
  const [banks, setBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [filter, setFilter] = useState('mes');
  const [customRange, setCustomRange] = useState({ from: todayISO(), to: todayISO() });
  const [editEntryId, setEditEntryId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!isOpen) return;
    setSupplierFilter('');
    setFilter('mes');
    setCustomRange({ from: todayISO(), to: todayISO() });
    setSelectedBank('');
  }, [isOpen]);
  const printRef = useRef();
  const { logoBase64, waitForReady } = useLogoPreloader(company?.logo);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    pageStyle: PRINT_PAGE_STYLE,
    onBeforeGetContent: () => waitForReady(4000),
  });

  const range = getRangeFromFilter(filter, customRange);

  useEffect(() => {
    if (!isOpen || !company.id) return;
    let cancelled = false;
    loadBanksWithCurrentBalance(company.id).then((data) => {
      if (cancelled) return;
      setBanks((data || []).filter((b) => b.status === STATUS.ATIVO));
    });
    return () => { cancelled = true; };
  }, [isOpen, company.id, refreshKey]);

  useEffect(() => {
    if (!isOpen || !company.id) return;
    let cancelled = false;
    Promise.all([
      loadStatementByCompany(company.id, {
        bankId: selectedBank || undefined,
        paymentDateFrom: range.from,
        paymentDateTo: range.to,
      }),
      loadNetPaidSince(company.id, range.from, selectedBank || undefined),
    ]).then(([data, net]) => {
      if (cancelled) return;
      setMovements(data || []);
      setNetSince(net || 0);
    });
    return () => { cancelled = true; };
  }, [isOpen, company.id, selectedBank, filter, customRange.from, customRange.to, refreshKey]);

  // Saldo de abertura do período = saldo atual (inicial + pagas) menos o líquido
  // pago dentro do período. getInitialBalance usa currentBalance (Ajuste 06).
  const saldoInicial = getInitialBalance(banks, selectedBank) - netSince;

  // Cobranças de OS pagas no mesmo evento (rateio) aparecem unificadas: 1
  // movimento por parcela com o valor somado, em vez de 1 linha por categoria.
  const grouped = groupStatementRows(movements);

  // Movimentações em ordem cronológica crescente, com saldo acumulado por linha
  // (RN04) calculado sobre TODO o período — independente do filtro de busca.
  const chronological = [...grouped].sort(
    (a, b) => (a.payment_date || '').localeCompare(b.payment_date || '')
  );
  let running = saldoInicial;
  const withBalance = chronological.map((inst) => {
    const value = Number(inst.value) || 0;
    const direction = inst.financial_entry?.type === ENTRY_TYPE.RECEITA ? 1 : -1;
    running += value * direction;
    return { inst, saldoAcumulado: running };
  });
  const saldoFinal = running;

  // Mantém o fornecedor selecionado nas opções mesmo quando ele sai do
  // período/banco atual — senão o select exibiria um valor inexistente.
  const supplierOptions = Array.from(
    new Set(
      movements
        .map((m) => m.financial_entry?.supplier_client)
        .filter(Boolean)
        .concat(supplierFilter ? [supplierFilter] : [])
    )
  ).sort((a, b) => a.localeCompare(b, 'pt-BR'));

  const filtered = withBalance.filter(({ inst }) => {
    if (!supplierFilter) return true;
    return inst.financial_entry?.supplier_client === supplierFilter;
  });

  const { sorted, sort, toggleSort } = useTableSort(filtered, SORT_ACCESSORS);

  if (!isOpen) return null;

  return (
    <>
    <div className="fd-modal-overlay" onClick={onClose}>
      <div className="fd-modal fd-modal--lg" onClick={(e) => e.stopPropagation()}>
        <div className="fd-modal-header">
          <div className="fd-modal-header-left">
            <h3 className="fd-modal-title">EXTRATO DO BANCO</h3>
          </div>
          <div className="fd-modal-header-right">
            {supplierOptions.length > 0 && (
              <select
                className="fd-installment-input"
                value={supplierFilter}
                onChange={(e) => setSupplierFilter(e.target.value)}
                style={{ minWidth: 160, maxWidth: 220 }}
              >
                <option value="">Todos os fornecedores</option>
                {supplierOptions.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            )}
            {banks.length > 0 && (
              <select
                className="fd-installment-input"
                value={selectedBank}
                onChange={(e) => setSelectedBank(e.target.value)}
                style={{ minWidth: 140 }}
              >
                <option value="">Todos os bancos</option>
                {banks.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            )}
            {onOpenProjected && (
              <button
                type="button"
                className="fd-btn-secondary"
                style={{ fontSize: 12, padding: '5px 16px', letterSpacing: 0.5 }}
                onClick={onOpenProjected}
                title="Ver entradas e saídas previstas no horizonte de até 90 dias"
              >
                📈 Projetado
              </button>
            )}
            {onNewEntry && (
              <button
                type="button"
                className="fd-btn-green"
                style={{ fontSize: 12, padding: '5px 16px', letterSpacing: 0.5 }}
                onClick={() => { onClose(); onNewEntry(); }}
              >
                + LANÇAMENTO
              </button>
            )}
            <button
              type="button"
              className="fd-btn-secondary"
              style={{ fontSize: 12, padding: '5px 16px', letterSpacing: 0.5 }}
              onClick={handlePrint}
              title="Imprimir ou gerar PDF do extrato"
            >
              Imprimir
            </button>
            <button className="fd-btn-close" type="button" onClick={onClose}>Fechar</button>
          </div>
        </div>

        <div className="fd-period-filters">
          <span className="fd-period-filters-label">Período:</span>
          {PERIOD_FILTERS.map((p) => (
            <button
              key={p.key}
              type="button"
              className={`fd-period-btn ${filter === p.key ? 'fd-period-btn--active' : ''}`}
              onClick={() => setFilter(p.key)}
            >
              {p.label}
            </button>
          ))}
          {filter === 'periodo' && (
            <div className="fd-period-range">
              <input
                type="date"
                className="fd-installment-input"
                value={customRange.from}
                onChange={(e) => setCustomRange({ ...customRange, from: e.target.value })}
              />
              <span style={{ color: '#999', fontSize: 11 }}>até</span>
              <input
                type="date"
                className="fd-installment-input"
                value={customRange.to}
                onChange={(e) => setCustomRange({ ...customRange, to: e.target.value })}
              />
            </div>
          )}
        </div>

        <div className="fd-modal-table-wrapper">
          <table className="fd-modal-table fd-statement-table">
            <thead>
              <tr>
                <SortableTh label="DATA PAGAMENTO" sortKey="data" sort={sort} onSort={toggleSort} />
                <SortableTh label="DESCRIÇÃO" sortKey="descricao" sort={sort} onSort={toggleSort} />
                <SortableTh label="BANCO" sortKey="banco" sort={sort} onSort={toggleSort} />
                <SortableTh label="TIPO" sortKey="tipo" sort={sort} onSort={toggleSort} style={{ textAlign: 'center' }} />
                <SortableTh label="VALOR" sortKey="valor" sort={sort} onSort={toggleSort} style={{ textAlign: 'right' }} />
                <SortableTh label="SALDO" sortKey="saldo" sort={sort} onSort={toggleSort} style={{ textAlign: 'right' }} />
              </tr>
            </thead>
            <tbody>
              <tr className="fd-statement-initial">
                <td>—</td>
                <td colSpan={3}>Saldo Inicial</td>
                <td style={{ textAlign: 'right' }}>—</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(saldoInicial)}</td>
              </tr>
              {sorted.map(({ inst, saldoAcumulado }) => {
                const isReceita = inst.financial_entry?.type === ENTRY_TYPE.RECEITA;
                return (
                  <tr
                    key={inst.id}
                    onClick={() =>
                      setEditEntryId(inst.financial_entry_id || inst.financial_entry?.id)
                    }
                    style={{ cursor: 'pointer' }}
                    title="Clique para abrir o lançamento"
                  >
                    <td>{formatDateBR(inst.payment_date)}</td>
                    <td>{inst.financial_entry?.supplier_client || '—'}</td>
                    <td className="fd-table-muted">{inst.Bank?.name || '—'}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span className="fd-status-badge" style={{
                        background: isReceita ? 'rgba(112,224,0,0.15)' : 'rgba(232,64,64,0.12)',
                        color: isReceita ? '#70E000' : '#e84040',
                      }}>
                        {isReceita ? 'ENTRADA' : 'SAÍDA'}
                      </span>
                    </td>
                    <td style={{
                      textAlign: 'right',
                      fontWeight: 600,
                      color: isReceita ? '#70E000' : '#e84040',
                    }}>
                      {isReceita ? '+ ' : '- '}{formatCurrency(inst.value)}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>
                      {formatCurrency(saldoAcumulado)}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="fd-table-empty">Nenhum lançamento encontrado</td>
                </tr>
              )}
            </tbody>
          </table>
          {filtered.length > 0 && (
            <div className="fd-statement-footer">
              <span>{filtered.length} {filtered.length === 1 ? 'movimentação' : 'movimentações'}</span>
              <span>·</span>
              <span style={{ color: '#70E000' }}>
                Entradas {formatCurrency(
                  filtered
                    .filter(({ inst }) => inst.financial_entry?.type === ENTRY_TYPE.RECEITA)
                    .reduce((s, { inst }) => s + (Number(inst.value) || 0), 0)
                )}
              </span>
              <span>·</span>
              <span style={{ color: '#e88c2a' }}>
                Saídas {formatCurrency(
                  filtered
                    .filter(({ inst }) => inst.financial_entry?.type === ENTRY_TYPE.DESPESA)
                    .reduce((s, { inst }) => s + (Number(inst.value) || 0), 0)
                )}
              </span>
              <span>·</span>
              <span>
                Saldo Final {formatCurrency(saldoFinal)}
              </span>
            </div>
          )}
        </div>

        <div style={{ position: 'absolute', left: '-9999px', top: 0, width: '1000px' }}>
          <BankStatementPrint
            ref={printRef}
            company={company}
            logoBase64={logoBase64}
            bankLabel={
              selectedBank
                ? banks.find((b) => String(b.id) === String(selectedBank))?.name || ''
                : 'Todos os bancos'
            }
            periodLabel={getPeriodLabel(filter, customRange)}
            supplierLabel={supplierFilter}
            saldoInicial={saldoInicial}
            saldoFinal={saldoFinal}
            rows={filtered}
          />
        </div>
      </div>
    </div>

    <FinancialEntryModal
      isOpen={editEntryId != null}
      entryId={editEntryId}
      onClose={() => setEditEntryId(null)}
      onSaved={() => {
        setRefreshKey((k) => k + 1);
        if (onChanged) onChanged();
      }}
    />
    </>
  );
};

export default CashFlowModal;
